import React, { useState } from 'react'

import { useQueryStation } from '@/api/stations/queries'

import PageHeader from '@/components/PageHeader'
import FilterChip from '@/components/FilterChip'

import { useParams, useNavigate } from 'react-router-dom'

import { Stack, Typography, Skeleton, Divider } from '@mui/material'

import { useBackButton } from '@/hooks/useBackButton'

import { ChargingPoint } from '@/types/ChargingPoint'

import ChargingPointItem from '@/modules/Station/ChargingPointItem'

import { chargingPointStatusUtils } from '@/utils/chargingPointStatus'

import { AppRoutes } from '@/routes/types/routes'
import { routeParser } from '@/routes/helpers/parser'

type ChargerFilter = 'ALL' | 'AVAILABLE' | 'OCCUPIED'

const StationChargersPage: React.FC = () => {
  const params = useParams()
  const navigate = useNavigate()

  const [filter, setFilter] = useState<ChargerFilter>('ALL')

  const stationId = params.stationId as string
  const backRoute = routeParser(AppRoutes.STATION, { stationId })

  useBackButton({ backRoute })

  const { data: station, isLoading: isStationLoading } = useQueryStation(
    {
      id: Number(stationId),
    },
    {
      enabled: Boolean(stationId),
      refetchInterval: 5000,
      retry: false,
    }
  )

  const chargingPoints = station?.charging_points ?? []
  const visibleChargingPoints = chargingPoints.filter((chargingPoint) => {
    if (filter === 'ALL') {
      return true
    }
    return chargingPointStatusUtils.getBucket(chargingPoint.status) === filter
  })
  const showEmptyFilterState = filter !== 'ALL' && visibleChargingPoints.length === 0

  const handleOnClickViewChargingPoint = (chargingPoint: ChargingPoint) => {
    navigate(
      routeParser(AppRoutes.CHARGING_POINT, {
        stationId: Number(stationId),
        chargingPointId: Number(chargingPoint.id),
      })
    )
  }

  return (
    <Stack paddingTop="24px" paddingX="24px">
      <Stack
        direction="column"
        spacing={2}
        sx={{
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <PageHeader title="Carregadores" backRoute={backRoute} />
        <Divider />
        {!isStationLoading && chargingPoints.length > 1 && (
          <Stack direction="row" spacing={1}>
            <FilterChip label="Todos" active={filter === 'ALL'} onClick={() => setFilter('ALL')} />
            <FilterChip label="Disponíveis" active={filter === 'AVAILABLE'} onClick={() => setFilter('AVAILABLE')} />
            <FilterChip label="Em uso" active={filter === 'OCCUPIED'} onClick={() => setFilter('OCCUPIED')} />
          </Stack>
        )}
        {isStationLoading ? (
          <Stack direction="column" spacing={1.5}>
            <Skeleton variant="rounded" width="100%" height={180} sx={{ borderRadius: 4 }} />
            <Skeleton variant="rounded" width="100%" height={180} sx={{ borderRadius: 4 }} />
          </Stack>
        ) : showEmptyFilterState ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" paddingY={2}>
            Nenhum conector encontrado para esse filtro.
          </Typography>
        ) : chargingPoints.length > 0 ? (
          <Stack direction="column" spacing={1.5}>
            {visibleChargingPoints.map((chargingPoint) => (
              <ChargingPointItem
                key={chargingPoint.id}
                chargingPoint={chargingPoint}
                onClick={handleOnClickViewChargingPoint}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" textAlign="center" paddingY={2}>
            Nenhum carregador cadastrado nesta estação.
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}

export default StationChargersPage
